import { ComponentRef, effect, inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';
import { TabViewService } from './tab-view.service';
import { buildRouteCacheKey, routeHasComponent } from './tab-view-route-key';

/**
 * `DetachedRouteHandle` is publicly typed as `{}`, but the router always
 * constructs it as `{ componentRef, route, contexts }` (see
 * `@angular/router`'s `activateRoutes`/`deactivateRouteAndItsChildren`). We
 * only need `componentRef` back out, to destroy it once a tab closes.
 */
interface InternalRouteHandle {
  componentRef: ComponentRef<unknown>;
}

/**
 * Keeps a route's component tree alive (detached, not destroyed) for any URL
 * that's an open tab in TabViewService — the "keep tabs hot" mechanism behind
 * the `~tabs` dev command. A complete no-op whenever tab mode is disabled, so
 * normal navigation elsewhere in the app is unaffected.
 *
 * Only reads from TabViewService (never the other way around) to avoid a
 * circular DI dependency — cleanup of a closed/evicted tab's cached component
 * is driven reactively off `tabs()` via the effect below, rather than
 * TabViewService calling back into this class.
 */
@Injectable({ providedIn: 'root' })
export class TabViewRouteReuseStrategy implements RouteReuseStrategy {
  private readonly tabViewService = inject(TabViewService);
  private readonly handles = new Map<string, InternalRouteHandle>();

  constructor() {
    effect(() => {
      const openUrls = new Set(this.tabViewService.tabs().map((t) => t.url));
      for (const key of [...this.handles.keys()]) {
        if (!openUrls.has(key)) this.destroy(key);
      }
    });
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    if (!this.tabViewService.enabled() || future.routeConfig !== curr.routeConfig) {
      // Unchanged default Angular behavior.
      return future.routeConfig === curr.routeConfig;
    }
    const futureKey = buildRouteCacheKey(future);
    if (!this.tabViewService.isOpenTabUrl(futureKey)) {
      // Not navigating into an open tab — an unrelated in-page link/redirect
      // sharing this route config. Scope the stricter check below to actual
      // tab switches only, so this keeps Angular's normal in-place param
      // update instead of forcing an unrelated full destroy/recreate.
      return true;
    }
    // Two open tabs can share the same route config (e.g. two dashboards both
    // matching /dashboard/:id). Angular's default check alone would treat
    // switching between them as an in-place param update on the still-live
    // component instead of a tab switch — skipping shouldDetach/shouldAttach
    // entirely and leaving the wrong tab's content on screen. Requiring the
    // resolved tab-cache key to match too forces the full detach/attach cycle
    // whenever the two snapshots are genuinely different tabs.
    return futureKey === buildRouteCacheKey(curr);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!this.tabViewService.enabled() || !routeHasComponent(route)) return false;
    return this.tabViewService.isOpenTabUrl(buildRouteCacheKey(route));
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!routeHasComponent(route)) return;
    const key = buildRouteCacheKey(route);
    if (handle === null) {
      this.handles.delete(key);
      return;
    }
    this.handles.set(key, handle as unknown as InternalRouteHandle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.tabViewService.enabled() || !routeHasComponent(route)) return false;
    return this.handles.has(buildRouteCacheKey(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!routeHasComponent(route)) return null;
    const handle = this.handles.get(buildRouteCacheKey(route));
    return (handle as unknown as DetachedRouteHandle) ?? null;
  }

  private destroy(urlKey: string): void {
    const handle = this.handles.get(urlKey);
    if (!handle) return;
    handle.componentRef.destroy();
    this.handles.delete(urlKey);
  }
}
